import { ALL_TOKENS, CHAINS } from 'consts';
import { store } from 'store';
import { ChainName, TokenInfo } from 'types';
import { ViewMode } from 'types/galaxy';

import { sophieDebugLog } from './sophieDebug';
import { AssistantAction } from './types';
import { setMicMuted } from './voiceManager';

// ── Valid values ────────────────────────────────────────────────────────────────

const VALID_VIEWS: Set<string> = new Set([
  'spiral',
  'tiers',
  'scatter',
  'helix',
  'sphere',
  'tornado',
  'grid',
  'sunflower',
  'dna',
  'spring',
  'clusters',
  'mind',
]);

const VALID_TABS = new Set(['wallet', 'wallets', 'positions', 'activity', 'settings']);

// Build chain lookup from CHAINS: by key, display name, and chain ID
const CHAIN_NAME_MAP: Record<string, ChainName> = {};
for (const [key, chain] of Object.entries(CHAINS) as [ChainName, { id: number; name: string }][]) {
  CHAIN_NAME_MAP[key.toLowerCase()] = key;
  CHAIN_NAME_MAP[chain.name.toLowerCase()] = key;
  CHAIN_NAME_MAP[String(chain.id)] = key;
}
// Extra aliases for natural language
Object.assign(CHAIN_NAME_MAP, {
  eth: 'ethereum',
  bnb: 'bsc',
  binance: 'bsc',
  'bnb smart chain': 'bsc',
  'binance smart chain': 'bsc',
  cro: 'cronos',
  arb: 'arbitrum',
  'arbitrum one': 'arbitrum',
  zeta: 'zetachain',
  matic: 'polygon',
  'manta pacific': 'manta',
  hyperliquid: 'hyper',
  hype: 'hyper',
  hyperevm: 'hyper',
  'unit zero': 'unit0',
  zerog: 'zeroG',
  '0g': 'zeroG',
  '0g mainnet': 'zeroG',
  'x layer': 'xLayer',
  xlayer: 'xLayer',
} as Record<string, ChainName>);

// ── Token search ────────────────────────────────────────────────────────────────

function findTokenByQuery(query: string, chainId: number): TokenInfo | undefined {
  const tokens = ALL_TOKENS[chainId] || [];
  const q = query.toLowerCase();

  // Exact symbol match (highest priority)
  const bySymbol = tokens.find((t) => t.symbol.toLowerCase() === q);
  if (bySymbol) return bySymbol;

  // Exact name match
  const byName = tokens.find((t) => t.name.toLowerCase() === q);
  if (byName) return byName;

  // Partial symbol match (e.g. "usdt" matches "USDT.e")
  const byPartialSymbol = tokens.find((t) => t.symbol.toLowerCase().startsWith(q));
  if (byPartialSymbol) return byPartialSymbol;

  // Partial name match
  const byPartialName = tokens.find((t) => t.name.toLowerCase().includes(q));
  if (byPartialName) return byPartialName;

  return undefined;
}

function resolveChainId(chainKey: string): number | null {
  const chainName = CHAIN_NAME_MAP[chainKey.toLowerCase()];
  if (chainName && CHAINS[chainName]) return CHAINS[chainName].id;
  return null;
}

// ── Action handler ──────────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function handleAction(action: AssistantAction, params?: Record<string, any>): void {
  switch (action) {
    // ── Wallet ─────────────────────────────────────────────────────────────────
    case 'connect_wallet': {
      const connect = store.assistantStore.actions['connect_wallet'];
      if (connect) connect();
      else console.warn('[Sophie] Hook action not registered:', action);
      break;
    }

    case 'disconnect_wallet': {
      const disconnect = store.assistantStore.actions['disconnect_wallet'];
      if (disconnect) disconnect();
      else console.warn('[Sophie] Hook action not registered:', action);
      break;
    }

    // ── Chain Switching ────────────────────────────────────────────────────────
    case 'switch_chain': {
      const chainKey = String(params?.value ?? params?.chain ?? '').toLowerCase();
      const chainName = CHAIN_NAME_MAP[chainKey];
      if (chainName && CHAINS[chainName]) {
        store.web3Store.setChainId(CHAINS[chainName].id);
      } else {
        console.warn('[Sophie] Unknown chain:', params?.chain);
      }
      break;
    }

    // ── Swap ───────────────────────────────────────────────────────────────────
    case 'flip_tokens':
      store.swapStore.flipTokens();
      break;

    case 'open_swap_settings':
      store.swapStore.openSettings();
      break;

    case 'close_swap_settings':
      store.swapStore.closeSettings();
      break;

    case 'set_slippage': {
      const slippage = Number(params?.value);
      if (!isNaN(slippage) && slippage >= 0 && slippage <= 50) {
        store.swapStore.setSettings({ slippage });
      } else {
        console.warn('[Sophie] Invalid slippage value:', params?.value);
      }
      break;
    }

    case 'toggle_pmm': {
      const enabled = params?.enabled;
      if (typeof enabled === 'boolean') {
        store.swapStore.setSettings({ usePMM: enabled });
      } else {
        store.swapStore.setSettings({ usePMM: !store.swapStore.settings.usePMM });
      }
      break;
    }

    case 'open_token_select': {
      const field = params?.field;
      if (field === 'in' || field === 'out') {
        store.swapStore.openTokenSelect(field);
      } else {
        store.swapStore.openTokenSelect('in');
      }
      break;
    }

    case 'close_token_select':
      store.swapStore.closeTokenSelect();
      break;

    case 'open_routing':
      store.swapStore.openRouting();
      break;

    case 'close_routing':
      store.swapStore.closeRouting();
      break;

    case 'set_swap_amount': {
      const value = String(params?.value ?? '');
      if (value) {
        store.swapStore.setValue(value);
      }
      break;
    }

    // ── News ───────────────────────────────────────────────────────────────────
    case 'open_news':
      store.galaxyStore.openNewsModal();
      break;

    case 'close_news':
      store.galaxyStore.closeNewsModal();
      break;

    // ── Galaxy View ────────────────────────────────────────────────────────────
    case 'galaxy_view': {
      const view = params?.view;
      if (view && VALID_VIEWS.has(view)) {
        store.galaxyStore.setViewMode(view as ViewMode);
      } else {
        console.warn('[Sophie] Unknown galaxy view:', view);
      }
      break;
    }

    case 'toggle_view_selector':
      store.galaxyStore.toggleViewSelector();
      break;

    // ── Portfolio ──────────────────────────────────────────────────────────────
    case 'portfolio_tab': {
      const tab = params?.tab;
      if (tab === null) {
        store.portfolioStore.setTab('wallet');
      } else if (tab && VALID_TABS.has(tab)) {
        store.commonStore.setMode('cockpit');
        store.portfolioStore.setTab(
          tab as 'wallet' | 'wallets' | 'positions' | 'activity' | 'settings'
        );
      } else {
        console.warn('[Sophie] Unknown portfolio tab:', tab);
      }
      break;
    }

    case 'refresh_portfolio':
      store.portfolioStore.updatePortfolio();
      break;

    case 'refresh_prices':
      store.commonStore.fetchPrices();
      break;

    // ── Copy Trading ───────────────────────────────────────────────────────────
    case 'toggle_autopilot':
      store.galaxyStore.toggleAutopilot();
      break;

    case 'toggle_pilot':
      store.galaxyStore.togglePilotMode();
      break;

    case 'open_deposit':
      store.copyFormStore.openCopyModal('deposit');
      break;

    case 'open_withdraw':
      store.copyFormStore.openCopyModal('withdraw');
      break;

    case 'close_deposit':
      store.copyFormStore.closeCopyModal();
      break;

    case 'set_deposit_amount': {
      const value = String(params?.value ?? '');
      if (value) {
        store.copyFormStore.setValue(value);
      }
      break;
    }

    // ── Trader Card ────────────────────────────────────────────────────────────
    case 'open_trader_card': {
      const index = Number(params?.index);
      if (!isNaN(index) && index >= 0) {
        store.galaxyStore.openTraderCard(index);
      }
      break;
    }

    case 'close_trader_card':
      store.galaxyStore.closeTraderCard();
      break;

    // ── Portal Flight ──────────────────────────────────────────────────────────
    case 'portal_flight':
      store.galaxyStore.requestPortalFlight();
      break;

    case 'wormhole_travel':
      store.galaxyStore.requestWormholeTravel();
      break;

    // ── Mic ────────────────────────────────────────────────────────────────────
    case 'mute_mic':
      setMicMuted(true);
      break;

    case 'unmute_mic':
      setMicMuted(false);
      break;

    // ── Assistant ──────────────────────────────────────────────────────────────
    case 'open_text_chat':
      store.assistantStore.setAssistantMode('text');
      break;

    case 'clear_chat':
      store.assistantStore.clearChatMessages();
      break;

    // ── Transaction Modal ──────────────────────────────────────────────────────
    case 'close_tx_modal':
      store.commonStore.closeTxModal();
      break;

    // ── Prepare Swap ──────────────────────────────────────────────────────────
    case 'prepare_swap': {
      const chain = params?.chain;
      const tokenIn = params?.token_in;
      const tokenOut = params?.token_out;
      const amount = params?.amount;

      // Step 1: Switch chain if specified
      if (chain) {
        const chainId = resolveChainId(chain);
        if (chainId) {
          store.web3Store.setChainId(chainId);
        } else {
          console.warn('[Sophie] prepare_swap: unknown chain:', chain);
        }
      }

      // Step 2: Switch to swap mode
      store.commonStore.setMode('swap');

      // Step 3: Set tokens and amount after chain-switch reactions complete
      setTimeout(() => {
        const targetChainId = store.web3Store.chainId;

        if (tokenIn) {
          const found = findTokenByQuery(tokenIn, targetChainId);
          if (found) {
            store.swapStore.selectToken('in', found.address);
            sophieDebugLog('🔍 Token in: ' + found.symbol + ' (' + found.address + ')');
          } else {
            console.warn('[Sophie] prepare_swap: token_in not found:', tokenIn);
          }
        }

        if (tokenOut) {
          const found = findTokenByQuery(tokenOut, targetChainId);
          if (found) {
            store.swapStore.selectToken('out', found.address);
            sophieDebugLog('🔍 Token out: ' + found.symbol + ' (' + found.address + ')');
          } else {
            console.warn('[Sophie] prepare_swap: token_out not found:', tokenOut);
          }
        }

        if (amount) {
          store.swapStore.setValue(String(amount));
        }
      }, 0);

      break;
    }

    // ── Show Top Traders ────────────────────────────────────────────────────────
    case 'show_top_traders':
      store.commonStore.setMode('main');
      store.galaxyStore.setViewMode('sphere');
      break;

    // ── AI Mind navigation ──────────────────────────────────────────────────────
    case 'fly_to_star': {
      const raw = params?.address ?? params?.wallet ?? params?.value;
      let index = Number(params?.index);
      if ((isNaN(index) || index < 0) && typeof raw === 'string') {
        const found = store.galaxyStore.traderIndexByAddress.get(raw.toLowerCase());
        index = found !== undefined ? found : -1;
      }
      if (!isNaN(index) && index >= 0) {
        store.commonStore.setMode('main');
        store.galaxyStore.requestFlyToStar(index);
        store.galaxyStore.openTraderCard(index);
      } else if (typeof raw === 'string' && raw.startsWith('0x')) {
        // Not in the displayed set — global lookup summons a guest star
        store.commonStore.setMode('main');
        void store.galaxyStore.searchWallet(raw);
      } else {
        console.warn('[Sophie] fly_to_star: trader not found:', raw ?? params?.index);
      }
      break;
    }

    case 'highlight_traders': {
      const list = params?.addresses ?? params?.wallets ?? params?.value;
      if (Array.isArray(list) && list.length > 0) {
        store.galaxyStore.setHighlight(list.map((a) => String(a)));
      } else {
        console.warn('[Sophie] highlight_traders: expected non-empty addresses array');
      }
      break;
    }

    case 'clear_highlight':
      store.galaxyStore.setHighlight(null);
      break;

    // ── Local state — logged for future migration ──────────────────────────────
    case 'scroll_chat_down':
      console.log(
        '[Sophie] Action "scroll_chat_down" requires local state migration (TextChat ref)'
      );
      break;

    case 'show_debug_panel':
      console.log(
        '[Sophie] Action "show_debug_panel" requires local state migration (Main.tsx useState)'
      );
      break;

    // ── Unknown ────────────────────────────────────────────────────────────────
    default:
      console.warn('[Sophie] Unknown action:', action);
  }

  sophieDebugLog('⚡ Action: ' + action + (params ? ' ' + JSON.stringify(params) : ''));
}
